import { useState } from 'react';
import { useT } from '@/store';
import { useAllVehicles } from '@/hooks/useFleet';
import { useDeleteServiceRecord, useServiceRecords } from '@/hooks/useServicing';
import { EmptyState, ErrorState, Panel, SkeletonRows } from '@/components/ui/Panel';
import { ConfirmButton } from '@/components/ui/ConfirmButton';
import { PaginationFooter } from '@/components/ui/TableToolbar';
import { useOffsetClamp, usePagedList } from '@/hooks/usePagedList';
import { ServiceFormModal } from '@/components/fleet/ServiceFormModal';
import { eurExact, formatDate, formatKm } from '@/lib/format';

/**
 * The service history itself — the rows every deadline on this tab is
 * computed from.
 *
 * WHAT WAS MISSING. `GET /api/tenants/<id>/service/records` has been paged,
 * filterable and tested since V-11, and so have the PATCH and DELETE beside
 * it. Nothing in frontend/src ever called any of the three. So a carrier
 * could WRITE service history — one form at a time, or by importing four
 * years of it through the bulk importer this tab already offers — and never
 * see a single row of it back, and never correct one. The same dead end as
 * the fuel POST with no screen (V-32) and the invoice PDF with no button
 * (V-42), on the table that decides when his trucks go into the workshop.
 *
 * WHAT THAT COSTS, measured on a live server rather than argued. One truck,
 * a 60 000 km oil interval, a current dated reading of 512 000 km, and one
 * record typed 550 000 where the workshop wrote 450 000 — one key along the
 * number row:
 *
 *     typed 550 000  ->  status "ok",      km_remaining +98 000
 *     true  450 000  ->  status "overdue", km_remaining  -2 000
 *
 * A truck 2 000 km past its oil change reads green, and goes on reading
 * green for another hundred thousand kilometres. ServiceFormModal's own
 * odometer hint promises that a wrong reading is "pataisoma per kelias
 * sekundes" — and until this panel there was no screen anywhere in the
 * product on which to do it.
 *
 * THE HONESTY RULES ARE RENDERING RULES HERE, as they are in ServicePanel
 * above. An empty odometer is an em-dash and never a 0, because a 0 tells
 * core/servicing.py the truck left the workshop with a fresh odometer. An
 * empty cost is an em-dash and never 0,00 €, because "0,00 € beside three
 * services" reads as warranty work rather than as three invoices nobody
 * typed in.
 *
 * AND THE ROWS WITH NO ODOMETER SAY SO OUT LOUD. `km_status: "unknown"`
 * with reason `last_service_has_no_odometer` is the most common complaint
 * ServicePanel makes, and it names a row — this row — without being able to
 * show it. The badge is what joins the two screens: the panel says a truck
 * cannot be judged, and the history says which line to open.
 */

/** The codebase's "no number at all". Never a blank cell, never a zero. */
const EM_DASH = '—';

/** The same sizing ServicePlanList gives its row actions, so the two tables
 *  on this tab are one control strip rather than two. */
const ACTION_BTN = 'btn !px-2.5 !py-1 !text-[11px]';

function RecordRow({ record, onEdit, plateFor }) {
  const t = useT();
  const remove = useDeleteServiceRecord();
  // The plate is LEFT JOINed by the route, so it is null for a record whose
  // truck has since been hard-deleted. The tenant's own vehicle list is
  // tried first anyway: it holds archived trucks too, and a service done to
  // a truck sold last spring is still the baseline for nothing but still
  // worth reading as history.
  const plate = record.license_plate ?? plateFor(record.vehicle_id) ?? EM_DASH;

  return (
    <tr>
      <td className="py-1.5 pr-3 tabular-nums whitespace-nowrap">
        {formatDate(record.performed_at)}
      </td>
      <td className="py-1.5 pr-3">{plate}</td>
      <td className="py-1.5 pr-3 font-medium">{record.service_type}</td>
      <td className="py-1.5 pr-3 tabular-nums whitespace-nowrap">
        {record.odometer_km === null || record.odometer_km === undefined ? (
          /* Not a bare em-dash. This is the row ServicePanel is complaining
             about when it prints "paskutinio serviso įraše neįvesta rida",
             and the badge is the only thing in the product that connects
             the complaint to the line that causes it. */
          <span
            className="rounded-full bg-accent/15 px-2 py-0.5 text-[9.5px] font-bold
                       uppercase tracking-[0.03em] text-accent"
          >
            {t('serviceHistory.noOdometer')}
          </span>
        ) : (
          formatKm(record.odometer_km)
        )}
      </td>
      <td className="py-1.5 pr-3 tabular-nums whitespace-nowrap">
        {/* eurExact() already answers null with the em-dash and 0 with
            "€0,00" — a branch here would be a second copy of that rule, and
            the one that drifted would be invisible. The distinction it keeps
            is the point: a workshop job done under warranty and an invoice
            nobody typed in are different facts. */}
        {eurExact(record.cost_eur)}
      </td>
      <td className="py-1.5 pr-3 text-dim">{record.workshop || EM_DASH}</td>
      <td className="py-1.5 text-right whitespace-nowrap">
        <div className="inline-flex items-center gap-1">
          <button
            type="button"
            className={ACTION_BTN}
            disabled={remove.isPending}
            onClick={() => onEdit(record)}
          >
            {t('common.editBtn')}
          </button>
          {/* The hint says what a delete MOVES, not merely that it is
              permanent. Removing the newest record of a job hands the next
              deadline back to the one before it, which can turn an "ok"
              row overdue the moment the dialog closes — and a carrier who
              cannot predict that does not press either button. */}
          <ConfirmButton
            label={t('common.deleteBtn')}
            confirmLabel={t('common.confirmDelete')}
            hint={t('serviceHistory.deleteMovesDeadline')}
            disabled={remove.isPending}
            onConfirm={() => remove.mutate(record.record_id)}
          />
        </div>
        {/* On the row that failed, not in a banner over the table: a shared
            message above forty records does not say which delete did not go
            through. */}
        {remove.isError ? <ErrorState error={remove.error} /> : null}
      </td>
    </tr>
  );
}

export function ServiceHistoryPanel() {
  const t = useT();
  const { limit, setLimit, offset, setOffset } = usePagedList();
  const [vehicleId, setVehicleId] = useState('');
  const [editing, setEditing] = useState(null);
  const { data: vehicles = [] } = useAllVehicles();

  // Filtered by the SERVER, like every other paged list here. A .filter()
  // over the loaded page would hide every record of the chosen truck from
  // row 26 onward while the footer went on counting all of them — a partial
  // answer that looks complete, which is the bug QuarryList already shipped
  // once with its category filter.
  const { data: records = [], totalCount, isLoading, isError, error } =
    useServiceRecords({ vehicleId, limit, offset });
  // V-296. Deleting the last record on a later page (RecordRow's own
  // ConfirmButton above) shrinks totalCount without ever touching offset —
  // without this, the panel reads a false "no service records at all" over
  // a truck's history that still has rows one page back.
  useOffsetClamp(totalCount, limit, offset, setOffset);

  const plateFor = (id) => vehicles.find((v) => v.vehicle_id === id)?.license_plate;

  return (
    <Panel
      title={t('serviceHistory.title')}
      actions={
        <select
          className="field !w-auto !py-1 !text-[11px]"
          value={vehicleId}
          aria-label={t('serviceHistory.vehicleFilterLabel')}
          onChange={(e) => {
            setVehicleId(e.target.value);
            // Back to the first page. Staying at offset 200 while switching
            // to a truck with nine services shows an empty table for a
            // filter that plainly matches something.
            setOffset(0);
          }}
        >
          <option value="">{t('serviceHistory.allVehicles')}</option>
          {vehicles.map((vehicle) => (
            <option key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
              {vehicle.license_plate}
              {vehicle.make_model ? ` · ${vehicle.make_model}` : ''}
            </option>
          ))}
        </select>
      }
    >
      {isError ? <ErrorState error={error} /> : null}
      {isLoading ? (
        <SkeletonRows rows={4} />
      ) : (
        <>
          <p className="mb-3 text-[11px] leading-snug text-dim">{t('serviceHistory.intro')}</p>
          {records.length === 0 ? (
            <EmptyState>
              {vehicleId ? t('serviceHistory.noneForVehicle') : t('serviceHistory.none')}
            </EmptyState>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[11.5px]">
                <thead className="text-[9.5px] uppercase tracking-[0.03em] text-dim">
                  <tr>
                    <th className="pb-1 pr-3 font-bold">{t('serviceHistory.colDate')}</th>
                    {/* The same word the due table above heads its plate
                        column with — a carrier reading down this tab must
                        not meet two names for the plate in three panels. */}
                    <th className="pb-1 pr-3 font-bold">{t('drivers.plateLabel')}</th>
                    <th className="pb-1 pr-3 font-bold">{t('servicing.colService')}</th>
                    <th className="pb-1 pr-3 font-bold">{t('serviceHistory.colOdometer')}</th>
                    <th className="pb-1 pr-3 font-bold">{t('serviceHistory.colCost')}</th>
                    <th className="pb-1 pr-3 font-bold">{t('serviceForm.workshopLabel')}</th>
                    <th className="pb-1 font-bold" />
                  </tr>
                </thead>
                <tbody>
                  {records.map((record) => (
                    <RecordRow
                      key={record.record_id}
                      record={record}
                      plateFor={plateFor}
                      onEdit={setEditing}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <PaginationFooter
        total={totalCount}
        limit={limit}
        offset={offset}
        onOffsetChange={setOffset}
        onLimitChange={setLimit}
      />

      {editing ? (
        <ServiceFormModal mode="record" record={editing} onClose={() => setEditing(null)} />
      ) : null}
    </Panel>
  );
}
